import angular from 'angular';

import introductionUrl from '../introduction.html';
import modalSchedulePublishedUrl from '../../education/modal-schedule-published.html';
import modalWelcomeToYourScheduleUrl from '../modal-welcome-to-your-schedule.html';
import { blow as blowConfetti } from '../../messages/directives/confetti';
import { EDUCATION_STEPS, TOAST_EDUCATION_STEPS } from '../constants';
import educationCreateShiftSvg from '../../assets/svg/UI/education-create-shift.svg';
import educationPlusSignSvg from '../../assets/svg/UI/education-plus-sign.svg';

export default class ShiftsEducation {
  constructor(
    Authentication,
    AccountService,
    AppBootstrap,
    Education,
    PersonaCollection,
    Utils,
    SlingAnalytics,
    SubscriptionUtils,
    sdk,
    $rootScope,
    $q,
    $uibModal,
    $timeout
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      AccountService,
      AppBootstrap,
      Education,
      PersonaCollection,
      Utils,
      SlingAnalytics,
      SubscriptionUtils,
      $rootScope,
      $q,
      $uibModal,
      $timeout,
    });

    this._onUpdateHandlers = [];
    this.USER_STATUSES = sdk('constants').USER_STATUSES;
  }

  init() {
    const educationStepsDict = {};
    const toastEducationStepsDict = {};
    const {
      CREATE_SHIFT,
      SAVE_NEW_SHIFT,
      UNPUBLISHED_SHIFT_CREATED,
      PUBLISH_SCHEDULE,
      FIELDS_VISIBILITY_BUTTONS,
      SHIFT_THREE_DOT_OPTIONS,
    } = EDUCATION_STEPS;

    class ShiftsEducationStep {
      constructor(
        content,
        { name, selector, chevron, offset, compile, noFooter = false, skip = () => false } = {}
      ) {
        Object.assign(this, { name, selector, chevron, offset, content, compile, skip, noFooter });
        educationStepsDict[name] = this;
      }
    }

    class ToastShiftsEducationStep {
      constructor(
        content,
        { name, next, selector, chevron, buttonLabel, skip = () => false } = {}
      ) {
        Object.assign(this, {
          name,
          next,
          selector,
          chevron,
          content,
          buttonLabel,
          skip,
        });
        toastEducationStepsDict[name] = this;
      }
    }

    this.toastEducationSteps = [
      new ToastShiftsEducationStep(
        `
        <div class="toast-shift-tooltip-text">
          You can undo your import by clicking this button if you prefer to build your schedule from scratch.
        </div>
        <span class="toast-shift-tooltip-footer-text">1 of 3</span>
      `,
        {
          name: TOAST_EDUCATION_STEPS.TOAST_IMPORT_SHIFT_UNDO,
          next: TOAST_EDUCATION_STEPS.TOAST_IMPORT_SHIFT_COPY,
          selector: '#undo-button',
          chevron: 'top-right',
          buttonLabel: 'Next',
        }
      ),
      new ToastShiftsEducationStep(
        `
        <div class="toast-shift-tooltip-text">
          You can copy your current week's schedule to a future week by clicking this button.
        </div>
        <span class="toast-shift-tooltip-footer-text">2 of 3</span>
      `,
        {
          name: TOAST_EDUCATION_STEPS.TOAST_IMPORT_SHIFT_COPY,
          next: TOAST_EDUCATION_STEPS.TOAST_IMPORT_SHIFT_PUBLISH,
          selector: '#copy-shifts-button',
          buttonLabel: 'Next',
          chevron: 'top-right',
        }
      ),
      new ToastShiftsEducationStep(
        `
        <div class="toast-shift-tooltip-text">
          Publish shifts so your employees can get notified, and view their new work schedules.
        </div>
        <span class="toast-shift-tooltip-footer-text">3 of 3</span>
      `,
        {
          name: TOAST_EDUCATION_STEPS.TOAST_IMPORT_SHIFT_PUBLISH,
          selector: '#education-publish-schedule',
          chevron: 'top-right',
        }
      ),
    ];

    if (!this._toastEducationHideListener) {
      this._toastEducationHideListener = this.$rootScope.$on('education:hide', (e, { config }) => {
        const next = toastEducationStepsDict[config?.name]?.next;
        if (next in toastEducationStepsDict) {
          this.$timeout(() => this.Education.show(toastEducationStepsDict[next]), 0);
        }
      });
    }

    this.educationSteps = [
      new ShiftsEducationStep(
        `
        <div>
          Create a shift by clicking <img src="${educationPlusSignSvg}" /> sign that appears when you hover over the schedule.
          <br><br>
          You can also create shifts by clicking the <img src="${educationCreateShiftSvg}" /> button at the top right.
        </div>
      `,
        {
          name: CREATE_SHIFT,
          selector: '#shift-calendar-day.is-today',
          chevron: this.getChevronForCreateShiftTooltip(),
          skip: () => this.clientFlags.shiftCreatedViaShiftModal,
        }
      ),
      new ShiftsEducationStep(
        `
        <div>
        Shifts aren’t visible to employees until they are published. Save the shift without publishing to continue working on your schedule.
        </div>
      `,
        {
          name: SAVE_NEW_SHIFT,
          chevron: 'right-bottom',
          offset: { x: -5, y: -5 },
          selector: '#education-add-shift',
          compile: true,
          noFooter: false,
        }
      ),
      new ShiftsEducationStep(
        `
        <div>
          To copy a shift, hold down
          <span class="ctrl-key-badge" ng-if="isMacOS">cmd ⌘</span>
          <span class="ctrl-key-badge" ng-else>ctrl</span>
          on your keyboard while dragging the shift over to a new date or employee.
        </div>
      `,
        {
          name: UNPUBLISHED_SHIFT_CREATED,
          chevron: 'left-bottom',
          scrollToElement: true,
          selector: '.education-recently-created-shift',
          compile: true,
        }
      ),
      new ShiftsEducationStep(
        `
        <div>
          Publish shifts so your employees can get notified, and view their new work schedules.
          <br><br>
          You can see at a glance if shifts are published or not; shifts that are not filled with color still need to be published.
        </div>
      `,
        {
          name: PUBLISH_SCHEDULE,
          selector: '#education-publish-schedule',
          chevron: 'top-right',
          skip: () => this.clientFlags?.dontShowAgain?.toastImportShiftsBanner,
        }
      ),
      new ShiftsEducationStep(
        `
        <div>
          Psst! We have moved a few options here to lighten up the shift modal ✨ Use them when needed.
        </div>
      `,
        {
          name: FIELDS_VISIBILITY_BUTTONS,
          offset: { x: -15, y: -3 },
          chevron: 'left-center',
          selector: '#education-fields-visibility',
          noFooter: false,
          forExistingUsersOnly: true,
        }
      ),
      new ShiftsEducationStep(
        `
        <div>
          Find more options to modify the shift under this menu.
        </div>
      `,
        {
          name: SHIFT_THREE_DOT_OPTIONS,
          offset: { x: -15, y: -3 },
          chevron: 'left-center',
          selector: '#education-shift-three-dot-options',
          noFooter: false,
          forExistingUsersOnly: true,
        }
      ),
    ];

    this.educationStepsDict = educationStepsDict;

    if (!this._educationHideListener) {
      this._educationHideListener = this.$rootScope.$on('education:hide', (e, { config }) => {
        if (config.name in this.educationStepsDict) {
          this.clientFlags[`onboarding4-${config.name}-presented`] = true;
          this.maybeCompleteEducation();
          this.Authentication.syncSettings();
        }
      });
    }
  }

  maybeCompleteEducation() {
    const allStepsPresented =
      this.educationSteps
        .filter((s) => s.forExistingUsersOnly)
        .map((s) => s.name)
        .every((name) => this.clientFlags[`onboarding4-${name}-presented`]) &&
      this.clientFlags.onboarding4ShiftPublishedEducationShown;
    if (allStepsPresented) {
      this.clientFlags.shiftsEducationCompleted = true;
      this.clientFlags.adminOnboardingCompleted = true;
      return true;
    }
  }

  shouldBePresented(name) {
    return (
      this.clientFlags.onboarding4Relevant &&
      !this.educationStepsDict[name].skip() &&
      !this.clientFlags[`onboarding4-${name}-presented`]
    );
  }

  get clientFlags() {
    return this.Authentication.user.settings.clientFlags;
  }

  get _educationStep() {
    return this.$rootScope.educationStep?.name ?? null;
  }

  async showOnboarding() {
    await this.AppBootstrap.initialRequestsLoaded;

    if (!this.clientFlags.adminOnboardingWelcomeShown && !this.Authentication.user.isOrgCreator) {
      const classes = 'sling-tour-modal sling-animate welcome-to-your-schedule';
      await this.$uibModal
        .open({
          windowClass: classes,
          templateUrl: modalWelcomeToYourScheduleUrl,
        })
        .result.ignoreCancel();
      this.clientFlags.adminOnboardingWelcomeShown = true;
      this.Authentication.syncSettings();
    }

    await this.$timeout(500);
    if (this.shouldBePresented(EDUCATION_STEPS.CREATE_SHIFT)) {
      this.Education.show(this.educationStepsDict[EDUCATION_STEPS.CREATE_SHIFT]);
    }
  }

  shouldShowOnboarding() {
    const shouldShowWelcomeModal =
      !this.clientFlags.adminOnboardingWelcomeShown && !this.Authentication.user.isOrgCreator;
    const shouldShowCreateShiftStep = this.shouldBePresented(EDUCATION_STEPS.CREATE_SHIFT);

    return Boolean(shouldShowWelcomeModal || shouldShowCreateShiftStep);
  }

  async showShiftsIntro({ isManager }) {
    // this stuff is for non-managers
    if (!this.clientFlags.shiftsIntroModalShown) {
      await this.$uibModal
        .open({
          windowClass: 'sling-tour-modal sling-animate',
          templateUrl: introductionUrl,
          controller: function ShowShiftInfoController() {
            this.isForManager = isManager;
          },
          controllerAs: 'ctrl',
        })
        .result.ignoreCancel();

      this.clientFlags.shiftsIntroModalShown = true;
      this.Authentication.syncSettings();
    }
  }

  trackAddShiftModalCancelled() {
    if (this._educationStep) {
      this.Education.hide();
    }
  }

  trackShiftModalAllDetailsProvided() {
    if (this.shouldBePresented(EDUCATION_STEPS.SAVE_NEW_SHIFT)) {
      this.Education.show(this.educationStepsDict[EDUCATION_STEPS.SAVE_NEW_SHIFT]);
    }
  }

  trackShiftEditModalOpen(shift) {
    if (shift.id && shift.status === 'published') {
      return;
    }

    let step;
    if (shift.id) {
      step = this.educationStepsDict[EDUCATION_STEPS.SHIFT_THREE_DOT_OPTIONS];
    } else {
      step = this.educationStepsDict[EDUCATION_STEPS.FIELDS_VISIBILITY_BUTTONS];
    }

    if (!this.clientFlags[`onboarding4-${step.name}-presented`]) {
      this.Education.show(step);
      return true;
    }
  }

  trackShiftScheduled() {
    if (this._educationStep) {
      this.Education.hide();
    }

    this._shiftJustCreatedViaModal = true;
  }

  async trackEventsProcessed(canPublishSchedule) {
    if (this.clientFlags.shiftsEducationCompleted || this._educationStep) {
      return;
    }

    await this.AppBootstrap.initialRequestsLoaded;

    const maybeToShowShiftCreatedEducation = () => {
      if (this.shouldBePresented(EDUCATION_STEPS.UNPUBLISHED_SHIFT_CREATED)) {
        this.Education.show(this.educationStepsDict[EDUCATION_STEPS.UNPUBLISHED_SHIFT_CREATED]);
        return true;
      }
    };

    const maybeToShowPublishScheduleEducation = () => {
      if (this.shouldBePresented(EDUCATION_STEPS.PUBLISH_SCHEDULE)) {
        this.Education.show(this.educationStepsDict[EDUCATION_STEPS.PUBLISH_SCHEDULE]);
        return true;
      }
    };

    if (this._shiftJustCreatedViaModal && !this._shiftsJustPublished) {
      await this.$timeout(0);
      if (!maybeToShowShiftCreatedEducation()) {
        maybeToShowPublishScheduleEducation();
      }
    } else if (this._shiftsJustPublished) {
      await this.$timeout(0);
      this.showPublishedScheduleModal();
    } else if (canPublishSchedule && !this.shouldBePresented(EDUCATION_STEPS.CREATE_SHIFT)) {
      maybeToShowPublishScheduleEducation();
    }

    this._shiftJustCreatedViaModal = false;
    this._shiftsJustPublished = false;
  }

  showPublishedScheduleModal() {
    if (
      !this.clientFlags.onboarding4Relevant ||
      this.clientFlags.onboarding4ShiftPublishedEducationShown ||
      !this.Authentication.user.isOrgCreator
    ) {
      return;
    }

    this.clientFlags.onboarding4ShiftPublishedEducationShown = true;
    this.maybeCompleteEducation();
    this.Authentication.syncSettings();
    blowConfetti(3000);

    const hasEmployees = this.PersonaCollection.users.length > 1;
    const SlingAnalytics = this.SlingAnalytics;
    this.$rootScope.isShiftEducationModalVisible = true;
    this.$uibModal
      .open({
        templateUrl: modalSchedulePublishedUrl,
        size: '500',
        controllerAs: 'ctrl',
        controller() {
          this.hasEmployees = hasEmployees;
          this.trackAction = (type) => {
            SlingAnalytics.track(SlingAnalytics.analyticsEvents.firstShiftCreated, type);
          };
        },
      })
      .result.finally(() => (this.$rootScope.isShiftEducationModalVisible = false));
  }

  trackShiftsCopy(shifts) {
    if (this.clientFlags.shiftsEducationCompleted) {
      return;
    }

    let unassigned = false;

    if (shifts.some((shift) => !shift.user)) {
      unassigned = this.unassignedShiftCreated();
    }

    const created = this.shiftsCreated(shifts.length);
    const copied = this.shiftsCopiedBetweenWeeks(shifts.length);

    if (unassigned || created || copied) {
      this.updateEducation();
    }
  }

  trackShiftDragAndDrop(shift) {
    if (this.clientFlags.shiftsEducationCompleted) {
      return;
    }

    let unassigned = false;

    if (!shift.user) {
      unassigned = this.unassignedShiftCreated();
    }

    // no id means that we've just copied a shift
    let created = false;
    let copied = false;
    if (!shift.id) {
      created = this.shiftsCreated();
      copied = this.shiftCopied();
    }

    if (unassigned || created || copied) {
      this.updateEducation();
    }

    if (this._educationStep && this._educationStep === EDUCATION_STEPS.UNPUBLISHED_SHIFT_CREATED) {
      this.Education.hide();
    }
  }

  trackShiftEdit(shift, publish) {
    let syncNeeded = false;

    if (shift.available) {
      syncNeeded = this.availableShiftCreated();
    }

    if (!shift.user) {
      syncNeeded = this.unassignedShiftCreated();
    }

    // creating a new shift
    if (!shift.id) {
      syncNeeded = this.shiftsCreated(1, { viaShiftModal: true });
    }

    if (publish) {
      syncNeeded = this.publishShifts();
    }

    if (syncNeeded) {
      this.updateEducation();
    }
  }

  trackMultiselectUse() {
    if (!this.clientFlags.shiftsEducationCompleted && this.multiselectHasBeenUsed()) {
      this.updateEducation();
    }
  }

  shiftsCreated(numberOfShiftsCreated = 1, { viaShiftModal = false } = {}) {
    if (!numberOfShiftsCreated) {
      return false;
    }

    if (
      this.clientFlags.numberOfShiftsCreated < 20 ||
      (!this.clientFlags.shiftCreatedViaShiftModal && viaShiftModal)
    ) {
      this.clientFlags.shiftsHasBeenCreated = true;
      this.clientFlags.numberOfShiftsCreated += numberOfShiftsCreated;
      if (viaShiftModal) {
        this.clientFlags.shiftCreatedViaShiftModal = true;
      }

      this.$rootScope.$broadcast('shifts-created', this.clientFlags.numberOfShiftsCreated);
      return true;
    }

    return false;
  }

  availableShiftCreated() {
    if (!this.clientFlags.shiftsAvailableShiftHasBeenCreated) {
      this.clientFlags.shiftsAvailableShiftHasBeenCreated = true;
      return true;
    }
    return false;
  }

  multiselectHasBeenUsed() {
    if (!this.clientFlags.shiftsMultiselectHasBeenUsed) {
      this.clientFlags.shiftsMultiselectHasBeenUsed = true;
      return true;
    }
    return false;
  }

  publishShifts(numberOfShiftsToPublish = 1) {
    if (this._educationStep && this._educationStep === EDUCATION_STEPS.PUBLISH_SCHEDULE) {
      this.Education.hide();
    }

    this._shiftsJustPublished = true;

    if (this.clientFlags.shiftsEducationCompleted || !numberOfShiftsToPublish) {
      return;
    }

    if (!this.clientFlags.shiftsHasBeenPublished) {
      this.clientFlags.shiftsHasBeenPublished = true;
    }

    if (!angular.isNumber(this.clientFlags.numberOfShiftsPublished)) {
      this.clientFlags.numberOfShiftsPublished = 0;
    }

    this.clientFlags.numberOfShiftsPublished += numberOfShiftsToPublish;
    return true;
  }

  shiftCopied() {
    if (!this.clientFlags.shiftHasBeenCopied) {
      this.clientFlags.shiftHasBeenCopied = true;
      return true;
    }
    return false;
  }

  shiftsCopiedBetweenWeeks() {
    if (!this.clientFlags.shiftsCopyBetweenWeeksHasBeenUsed) {
      this.clientFlags.shiftsCopyBetweenWeeksHasBeenUsed = true;
      return true;
    }
    return false;
  }

  unassignedShiftCreated() {
    if (!this.clientFlags.shiftsUnassignedShiftHasBeenCreated) {
      this.clientFlags.shiftsUnassignedShiftHasBeenCreated = true;
      return true;
    }
    return false;
  }

  updateEducation() {
    if (this.Authentication.user.settings.clientFlags.shiftsEducationCompleted) {
      return;
    }

    this.Authentication.syncSettings();
    this._onUpdateHandlers.forEach((h) => h());
  }

  onUpdate(fn) {
    this._onUpdateHandlers.push(fn);

    return () => {
      this._onUpdateHandlers = this._onUpdateHandlers.filter((h) => h !== fn);
    };
  }

  getChevronForCreateShiftTooltip() {
    const todaysDayOfTheWeek = this.Utils.getNowDate().getDay();
    const firstDayOfWeek = this.Authentication.user?.orgSettings.firstDayOfWeek;

    const grid = [0, 1, 2, 3, 4, 5, 6];
    const dayIndex = grid.findIndex((item) => item === firstDayOfWeek);

    const partOne = grid.slice(dayIndex);
    const partTwo = grid.slice(0, dayIndex);
    const orgGrid = [...partOne, ...partTwo];

    const todayIndex = orgGrid.findIndex((item) => item === todaysDayOfTheWeek);

    return todayIndex >= 4 ? 'top-right' : 'left-top';
  }

  showToastImportShiftEducation() {
    if (!this.clientFlags?.dontShowAgain?.toastScheduleImportToolTips) {
      this.Education.show(this.toastEducationSteps[0]);
      this.dontShowToastImportShiftEducationAgain();
    }
  }

  dontShowToastImportShiftEducationAgain() {
    this.$timeout(() => {
      this.clientFlags.dontShowAgain = {
        ...this.clientFlags.dontShowAgain,
        toastScheduleImportToolTips: true,
      };
      this.Authentication.syncSettings();
    }, 500);
  }
}
